import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { track } from "@/lib/tracking";

/**
 * PayPal alternative checkout. Renders only when PayPal is configured (server /paypal/config).
 * Props: { country, postal, shippingOption, discountCode, currency = "EUR" }
 */
export function PayPalPanel({ country, postal, shippingOption, discountCode, currency = "EUR", phone, idDocument, needsIdDoc, onError }) {
  const { token, customer } = useAuth();
  const { items, referralCode, clear } = useCart();
  const { i18n } = useTranslation();
  const [cfg, setCfg] = useState({ enabled: false, client_id: "", mode: "sandbox" });

  useEffect(() => {
    api.get("/paypal/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  if (!cfg.enabled || !cfg.client_id) return null;
  if (!items.length) return null;

  const cartPayload = items.map((i) => ({ product_id: i.id, quantity: i.quantity }));

  return (
    <div className="pt-3 border-t border-white/10" data-testid="paypal-panel">
      <p className="text-xs uppercase tracking-widest text-white/50 mb-3">or pay with</p>
      {needsIdDoc && (
        <p data-testid="paypal-customs-reminder" className="flex items-start gap-1.5 text-[11px] leading-snug text-amber-200/80 mb-3">
          Outside the EU, all import taxes and customs duties are charged by the destination country and are the buyer's sole responsibility.
        </p>
      )}
      <PayPalScriptProvider
        options={{
          "client-id": cfg.client_id,
          currency,
          intent: "capture",
          components: "buttons",
        }}
      >
        <PayPalButtons
          style={{ layout: "horizontal", color: "gold", shape: "pill", label: "paypal", height: 44 }}
          createOrder={async () => {
            if (!(phone || "").trim()) {
              toast.error("Please enter a phone number before paying.");
              throw new Error("missing phone");
            }
            if (needsIdDoc && !(idDocument || "").trim()) {
              toast.error("Please enter your ID / passport number (required for international customs).");
              throw new Error("missing id document");
            }
            if ((idDocument || "").trim() && !/^[A-Za-z0-9]{5,20}$/.test((idDocument || "").trim().replace(/\s/g, ""))) {
              toast.error("The ID / passport number looks invalid. Use 5–20 letters and numbers (no symbols).");
              throw new Error("invalid id document");
            }
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const { data } = await api.post("/paypal/create-order", {
              items: cartPayload,
              origin_url: window.location.origin,
              currency,
              shipping_country: country,
              shipping_option: shippingOption,
              postal_code: postal,
              discount_code: discountCode || undefined,
              referral_code: referralCode || undefined,
              email: customer?.email,
              phone: (phone || "").trim(),
              id_document: needsIdDoc ? (idDocument || "").trim() : undefined,
              locale: i18n.language || "en",
            }, { headers });
            track("begin_checkout", { provider: "paypal", value: data.amount, currency: data.currency });
            return data.paypal_order_id;
          }}
          onApprove={async (data) => {
            try {
              const { data: cap } = await api.post("/paypal/capture", { paypal_order_id: data.orderID });
              if (cap.status === "COMPLETED") {
                track("purchase", { provider: "paypal", transaction_id: cap.session_id });
                clear();
                window.location.href = `/payment/success?session_id=${cap.session_id}`;
              } else {
                toast.error("Payment not completed. Please try again.");
              }
            } catch (e) {
              toast.error("Could not capture PayPal payment.");
            }
          }}
          onError={(err) => {
            if (onError) onError(err);
            toast.error("PayPal error. Please try Stripe or contact support.");
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
}
